import React from 'react';
import { Alert, FormGroup } from 'reactstrap';
import cls from 'classnames';
import { getCounty } from '@s20/common/util/location';
import { getZipPattern } from '@s20/common/util/validation';
import { httpClient } from '@s20/services/httpClient';
import CommonContext, { ApiRoutes } from '../Common';
import { Input, FormLabel } from '../common/forms/FormElements';
import ValidatedSelect from '../common/forms/ValidatedSelect';
import { CountyLabel } from '../uscounties/CountyLabel';

export default class AddressInput extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);

        this.state = {
            usStates: [],
            countries: [],
            zipPattern: '',
        };

        this.handleCountryChange = this.handleCountryChange.bind(this);
    }

    componentDidMount() {
        this.populateState();
    }

    componentDidUpdate(prevProps) {
        // Detect changes to the countryId prop
        if (prevProps.address?.countryId !== this.props.address?.countryId) {
            this.populateState();
        }
    }

    async populateState() {
        if (this.props.showCountryInput) {
            const [countries] = await Promise.all([
                httpClient.js(ApiRoutes.typeAheads.countries()),
            ]);

            this.setState({ countries });
        }

        if (this.props.address?.countryId) {
            const [usStates] = await Promise.all([
                httpClient.js(ApiRoutes.typeAheads.USStatesbyCountryId(this.props.address.countryId)),
            ]);

            this.setState({ usStates, zipPattern: getZipPattern(this.props.address.countryId) });
        }
    }

    async handleCountryChange(selection) {
        const country = selection?.id || '';
        this.setState({ zipPattern: '', usStates: [] });

        if (country) {
            const usStates = await httpClient.js(
                ApiRoutes.typeAheads.USStatesbyCountryId(country),
            );

            this.setState({ zipPattern: getZipPattern(country), usStates });
        }

        if (this.props.onCountryChanged) {
            this.props.onCountryChanged(selection);
        }
    }

    render() {
        const {
            address, namePrefix, isCountyCaptureEnabled, usCounties, workTypeId,
        } = this.props;

        const { zipPattern, usStates, countries } = this.state;
        const prefixName = namePrefix ? `${namePrefix}.` : '';
        const colClass = cls(this.props.className ?? '', {
            'col-6': !this.props.single,
        });

        const county = getCounty(usCounties, {
            usCountyId: address.usCountyId,
            contractUnionWorkTypeId: workTypeId,
        });

        return (
            <>
                <Input
                    formGroupClassName={colClass}
                    label="Address Line 1"
                    id="address1"
                    name={`${prefixName}address1`}
                    onFocus={(e) => {
                        e.currentTarget.select();
                    }}
                    value={address.address1 ?? ''}
                    onChange={this.props.onChange}
                    placeholder="Enter Address Line 1"
                    required
                    error="Address Line 1 is required."
                />

                <Input
                    formGroupClassName={colClass}
                    label="Address Line 2"
                    id="address2"
                    name={`${prefixName}address2`}
                    value={address.address2 ?? ''}
                    onChange={this.props.onChange}
                    placeholder="Enter Address Line 2"
                />

                <Input
                    formGroupClassName={colClass}
                    label="City"
                    id="city"
                    name={`${prefixName}city`}
                    required
                    placeholder="Enter City"
                    value={address.city ?? ''}
                    onChange={this.props.onChange}
                    error="City is required."
                />

                {Boolean(this.props.showCountryInput) && (
                    <ValidatedSelect
                        id="country"
                        label="Country"
                        formGroupClassName={colClass}
                        name={`${prefixName}country`}
                        required
                        options={countries}
                        value={
                            countries.find((s) => s.value === address.countryCode)
                            ?? ''
                        }
                        onChange={this.handleCountryChange}
                        error="A country selection is required."
                    />
                )}

                <ValidatedSelect
                    id="state"
                    label="State"
                    name={`${prefixName}state`}
                    formGroupClassName={colClass}
                    required
                    options={usStates}
                    value={
                        usStates.find((s) => s.value === address.state)
                        ?? ''
                    }
                    onChange={this.props.onStateChanged}
                    isDisabled={!this.props.address.countryCode && this.props.showCountryInput}
                    error="A state selection is required."
                />

                <Input
                    formGroupClassName={colClass}
                    label="ZIP Code"
                    id="zip"
                    name={`${prefixName}zip`}
                    placeholder="Enter ZIP Code"
                    pattern={zipPattern}
                    value={address.zip ?? ''}
                    onChange={this.props.onChange}
                    required
                    error={!address.zip ? 'ZIP Code is required.' : 'ZIP Code is invalid.'}
                />

                {isCountyCaptureEnabled && (
                    <FormGroup className={colClass}>
                        <FormLabel
                            htmlFor="countyName"
                            label="County"
                            required
                        />

                        <Alert
                            color="info"
                            className="mb-0"
                        >
                            <CountyLabel {...county} />
                        </Alert>

                        <Input
                            id="countyName"
                            name={`${prefixName}usCountyId`}
                            value={address.usCountyId ?? ''}
                            hidden
                            required
                        />

                        <small className="invalid-feedback text-danger">
                            County is required
                        </small>
                    </FormGroup>
                )}
            </>
        );
    }
}